export type TicketMessageDisplayInput = {
  id: string
  role: 'line_manager' | 'expert' | 'system'
  bodyRaw: string
  bodySanitized: string
  authorName: string | null
  authorEmail: string | null
}

export type ClarificationAnswer = {
  question: string
  answer: string
}

export type TicketMessageDisplayItem = TicketMessageDisplayInput & {
  clarificationAnswers: ClarificationAnswer[] | null
  displayText: string
}

export type TicketMessageDisplayGroup = {
  id: string
  role: TicketMessageDisplayInput['role']
  authorLabel: string
  items: TicketMessageDisplayItem[]
}

function stripHtml(text: string) {
  return text.replace(/<[^>]*>/g, '').trim()
}

export function formatTicketMessageDisplayText(bodyRaw: string) {
  return stripHtml(bodyRaw)
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export function getTicketMessageAuthorLabel(message: TicketMessageDisplayInput) {
  if (message.role === 'system') return 'System'
  if (message.role === 'line_manager') {
    return message.authorName?.trim() || message.authorEmail?.trim() || 'Submitter'
  }
  return message.authorName?.trim() || 'Expert'
}

export function parseClarificationAnswers(bodyRaw: string): ClarificationAnswer[] | null {
  const sections = bodyRaw
    .split(/\n\s*---\s*\n/g)
    .map((section) => section.trim())
    .filter(Boolean)

  if (sections.length < 2) return null

  const answers = sections.map((section) => {
    const match = section.match(/^\d+\.\s*([\s\S]*?)\n{2,}([\s\S]*)$/)
    if (!match) return null

    const question = stripHtml(match[1])
    const answer = stripHtml(match[2])
    if (!question || !answer) return null

    return { question, answer }
  })

  if (answers.some((answer) => answer === null)) return null
  return answers as ClarificationAnswer[]
}

export function groupTicketMessages(
  messages: TicketMessageDisplayInput[],
): TicketMessageDisplayGroup[] {
  const groups: TicketMessageDisplayGroup[] = []

  for (const message of messages) {
    const authorLabel = getTicketMessageAuthorLabel(message)
    const item: TicketMessageDisplayItem = {
      ...message,
      clarificationAnswers: parseClarificationAnswers(message.bodyRaw),
      displayText: formatTicketMessageDisplayText(message.bodyRaw),
    }
    const lastGroup = groups.at(-1)

    if (lastGroup && lastGroup.role === message.role && lastGroup.authorLabel === authorLabel) {
      lastGroup.items.push(item)
      continue
    }

    groups.push({
      id: message.id,
      role: message.role,
      authorLabel,
      items: [item],
    })
  }

  return groups
}
